import React from 'react';
import {PropTypes} from 'prop-types';
import moment from 'moment-timezone';
import numeral from 'numeral';
import {ErrorsBlockComponent} from '../../shared/errorsBlockComponent';
import {ButtonBusyIndicatorComponent} from '../../shared/buttonBusyIndicatorComponent';
import {Link} from '../../shared/link';
import {Pagination} from '../../shared/Pagination';
import {PageHeader} from '../../shared/PageHeader';
import {MakeApiCall, WriteConsoleErrors, ToString, ObjectToQueryString, ReplaceUrl} from '../../shared/utils';
import {FlagComponent} from '../AccountsListComponent/flag';
import {AccountComponent} from '../AccountsListComponent/account';
import {SimpleFilterItemComponent} from '../SimpleFilterItem';
import {PAYMENT_STATE} from '../../app.defs';
import {UrlBuilder} from '../../app.defs';

import './styles.scss';

const PAYMENTS_ACTION = {
    NONE: 0,
    LOAD: 1
}

export class PaymentsListComponent extends React.Component {
    constructor(props, ...others) {
        super(props, ...others);
        this.state = {
            isLoaded: false,
            accountFilter: props.accountFilter,
            paymentFilter: props.paymentFilter,
            paymentStateId: props.paymentStateId,
            paymentTypeId: props.paymentTypeId,
            pageNum: props.pageNum,
            pageSize: props.pageSize,
            action: PAYMENTS_ACTION.NONE,
            totalCount: 0,
            errors: null,
            payments: [],
            requestId: 0
        }

        this.doLoad = this.doLoad.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.renderForm = this.renderForm.bind(this);
        this.renderTable = this.renderTable.bind(this);
        this.renderPagination = this.renderPagination.bind(this);
    }

    componentDidMount() {
        const self = this;

        self._isMounted = true;
        self.doLoad();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    doLoad() {
        const self = this;
        if (!self._isMounted) {
            return;
        }

        const requestId = self.state.requestId + 1;

        const data = {
            accf: self.state.accountFilter,
            payf: self.state.paymentFilter,
            payt: self.state.paymentTypeId,
            pays: self.state.paymentStateId,
            pn: self.state.pageNum,
            ps: self.state.pageSize
        }

        new Promise(resolve => {
            self.setState({action: PAYMENTS_ACTION.LOAD, errors: null, requestId: requestId}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                ReplaceUrl({
                    pathname: window.location.pathname,
                    search: ObjectToQueryString(data, true, {pn:1, ps: 50})
                });

                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getPayments',
                            data: data,
                            dataType: 'json',
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted || (self.state.requestId != requestId)
                );
            }
        })
        .then(response => {
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({
                    action: PAYMENTS_ACTION.NONE,
                    isLoaded: true,
                    accountFilter: response.accf,
                    paymentFilter: response.payf,
                    paymentStateId: response.pays,
                    paymentTypeId: response.payt,
                    pageNum: response.pn,
                    pageSize: response.ps,
                    payments: response.Payments,
                    totalCount: response.TotalCount
                });
            }
        })
        .catch(errors => {
            WriteConsoleErrors(errors, true, false);
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({action: PAYMENTS_ACTION.NONE, errors: errors})
            }
        });
    }

    handleSubmit(event) {
        event.preventDefault();
        this.doLoad();
    }

    renderForm() {
        const self = this;
        const {action, accountFilter, paymentFilter, paymentStateId, paymentTypeId} = self.state;
        const isDisabled = action != PAYMENTS_ACTION.NONE;

        const _renderFilterLink = (filter, negativeFilter, filterPattern) => {
            return (
                <SimpleFilterItemComponent
                    disabled={isDisabled}
                    filters={paymentFilter}
                    filter={filter}
                    detectPattern={filterPattern}
                    negativeDetectPattern={negativeFilter}
                    onChange={f => self.setState({paymentFilter: f})}
                />
            )
        }

        return (
            <div className="mb-n2 mt-2">
                <form className="form-inline" onSubmit={self.handleSubmit}>
                    <input 
                        type="text"
                        className="form-control mr-2 mb-2"
                        placeholder="PaymentId, TransactionId"
                        style={{width: '250px', maxWidth: '100%'}}
                        disabled={isDisabled}
                        value={ToString(paymentFilter)}
                        onChange={e => self.setState({paymentFilter: e.target.value})}
                    />
                    <input 
                        type="text"
                        className="form-control mr-2 mb-2"
                        placeholder="AccountId, Email, Name, Company"
                        style={{width: '400px', maxWidth: '100%'}}
                        disabled={isDisabled}
                        value={ToString(accountFilter)}
                        onChange={e => self.setState({accountFilter: e.target.value})}
                    />
                    <select
                        className="custom-select mr-2 mb-2"
                        disabled={isDisabled}
                        value={ToString(paymentTypeId)}
                        onChange={e => self.setState({paymentTypeId: e.target.value})}
                    >
                        {self.props.paymentTypes.map(p => <option key={p.Key} value={p.Key}>{p.Value}</option>)}
                    </select>
                    <select
                        className="custom-select mr-2 mb-2"
                        disabled={isDisabled}
                        value={ToString(paymentStateId)}
                        onChange={e => self.setState({paymentStateId: e.target.value})}
                    >
                        {self.props.paymentStates.map(p => <option key={p.Key} value={p.Key}>{p.Value}</option>)}
                    </select>
                    <button 
                        type="submit"
                        className="btn btn-outline-primary mb-2"
                        disabled={isDisabled}
                    >
                        <span className="fa fa-search mr-1" />
                        Search
                        <ButtonBusyIndicatorComponent visible={action == PAYMENTS_ACTION.LOAD} />
                    </button>
                </form>
                <div className="small">
                    Filters:&nbsp;
                    <span className="mre-1 text-nowrap d-inline-block">{_renderFilterLink(':in', ':out')} — incoming only;</span>
                    <span className="mre-1 text-nowrap d-inline-block">{_renderFilterLink(':out', ':in')} — outgoing only;</span>
                    <span className="mre-1 text-nowrap d-inline-block">{_renderFilterLink(':auto', ':nauto')}, {_renderFilterLink(':nauto', ':auto')} — auto or not;</span>
                </div>
            </div>
        )
    }

    renderTable() {
        const self = this;
        const {action, payments} = self.state;
        const {timeZoneId} = self.props;
        
        const tzNow = moment.utc().tz(timeZoneId);
        const tzName = tzNow.format('z');
        const existsNew = payments.some(p => tzNow.isSame(moment.utc(p.CreatedAt).tz(timeZoneId), 'day'));

        return (
            <table className="payments table-striped-even table-hover table-responsive newstyle">
                <thead>
                    <tr>
                        {existsNew && <th width="1%" />}
                        <th width="1%">Payment#</th>
                        <th width="1%">Transaction#</th>
                        <th width="1%" className="text-nowrap">Created ({tzName})</th>
                        <th width="1%" className="text-right text-nowrap" colSpan={2}>Amount</th>
                        <th width="1%">Type</th>
                        <th width="35%">Account</th>
                        <th width="1%" className="text-center"><span className="fa fa-globe" /></th>
                        <th width="25%">Email</th>
                        <th width="30%">Company</th>
                        <th width="1">State</th>
                    </tr>
                </thead>
                <tbody>
                    {payments.map(p => {
                        let rowClass = '';
                        switch (p.PaymentStateId) {
                            case PAYMENT_STATE.Pending:
                                rowClass = 'warning';
                                break;
                            case PAYMENT_STATE.Error:
                                rowClass = 'danger';
                                break;
                        }

                        return (
                            <tr key={'payment-' + p.Id} className={rowClass}>
                                {existsNew && 
                                    <td>
                                        {tzNow.isSame(moment.utc(p.CreatedAt).tz(timeZoneId), 'day') && <span className="fa fa-flash text-success" />}
                                    </td>
                                }
                                <td className="text-nowrap">
                                    <Link 
                                        to={UrlBuilder.PaymentInfo({PaymentId: p.Id})}
                                        className="mre-2"
                                    >
                                        {p.Id}
                                    </Link>
                                    {Boolean(p.IsAuto) && <span className="text-warning">(auto)</span>}
                                </td>
                                <td className="text-nowrap">{p.TransactionId}</td>
                                <td className="text-nowrap">{moment.utc(p.CreatedAt).tz(timeZoneId).format('L LT')}</td>
                                <td width="40" className={'text-right font-weight-bold text-nowrap pr-0' + (p.Amount < 0 ? ' text-danger' : '')}>
                                    {numeral(p.Amount).USD()}
                                </td>
                                <td width="1" className="text-left font-weight-bold text-nowrap">
                                    {Boolean(p.Fee) && <sup className="mle-1">{numeral(p.Fee).USD()}</sup>}
                                </td>
                                <td className="text-nowrap">{p.PaymentTypeName}</td>
                                <td>
                                    <AccountComponent 
                                        id={p.Account.Id}
                                        name={p.Account.Name}
                                        showId
                                        showLoginAs
                                        linkToInfo
                                    />
                                </td>
                                <td> 
                                    <FlagComponent
                                        countryId={p.Account.CountryId}
                                        countryName={p.Account.CountryName}
                                        lastIP={p.Account.LastIP}
                                    />
                                </td>
                                <td>{p.Account.Email}</td>
                                <td>{p.Account.Company}</td>
                                <td>{(p.PaymentStateId != PAYMENT_STATE.Success) && p.PaymentStateName}</td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        )
    }

    renderPagination() {
        const self = this;
        const {pageNum, pageSize, totalCount}  = self.state;

        const pagesCount = Math.ceil(totalCount / Math.max(1, pageSize));

        if (pagesCount <= 1) {
            return null;
        }

        return (
            <Pagination
                className="justify-content-end"
                pagesCount={pagesCount}
                pageNum={pageNum}
                maxButtons={7}
                onPageSelect={(pn) => self.setState({pageNum: pn}, self.doLoad)}
                size="sm"
            />
        )
    }

    render() {
        const self = this;
        const {totalCount, isLoaded, errors} = self.state;

        return (
            <div>
                <PageHeader
                    fixed
                    captionTruncate
                    notWrappedMaxHeight={null}
                    caption={
                        <h2 className="text-truncate">Payments</h2>
                    }
                >
                    {self.renderForm()}
                </PageHeader>
                <ErrorsBlockComponent errors={errors} />
                {isLoaded && <div className="small">{`${totalCount} payment(s) found`}</div>}
                {self.renderTable()}
                <div className="pt-3">
                    {self.renderPagination()}
                </div>
           </div>
        )
    }
}
